import React, { useState } from 'react'
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  Modal,
  styled,
  Typography,
  Paper,
  IconButton,
  Chip,
  Alert,
  Fade,
  Select,
  MenuItem,
  FormControlLabel,
  DialogActions,
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCloudArrowUp,
  faFilePdf,
  faFile,
  faXmark,
  faCircleExclamation,
  faCircleCheck,
} from '@fortawesome/pro-solid-svg-icons'
import { BorderButtonPurple, CustomButton, SwitchButton } from '../buttons'
import lightTheme from '../../styles/theme/lightTheme'
import '../../styles/components/_box.scss'
import '../../styles/components/_grids.scss'
import '../../styles/components/_inputs.scss'
import { ButtonFilesUpload } from '../inputs'
import Api from '../../services/api.service'
import { handlingErrors } from '../../utils'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import * as yup from 'yup'
import { useDispatch } from 'react-redux'
import { userLogout } from '../../store/slices/userAuthReducer'
import { useTranslation } from 'react-i18next'
import { ROLE_CANDIDAT, ROLE_ENTREPRISE } from '../../data'
import GradientButton from '../buttons/GradientButton'
import OutlineButton from '../buttons/OutlineButton'

const InputContainer = styled(FormControl)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}))

const UploadArea = styled(Paper)(({ theme }) => ({
  padding: '32px',
  border: '2px dashed #96A0FF',
  borderRadius: '12px',
  backgroundColor: '#F8F9FF',
  textAlign: 'center',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#808BF8',
    backgroundColor: '#F0F2FF',
  },
}))

const FileItem = styled(Paper)(({ theme, isValid }) => ({
  padding: '16px',
  borderRadius: '8px',
  backgroundColor: isValid ? '#FFFFFF' : '#FFF5F5',
  border: `1px solid ${isValid ? '#E5E7EB' : '#FCA5A5'}`,
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  transition: 'all 0.2s ease',
  flexWrap: 'wrap',
  '&:hover': {
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
}))

const FileIcon = styled(Box)(({ theme, fileType }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: fileType === 'application/pdf' ? '#FEE2E2' : '#DBEAFE',
  color: fileType === 'application/pdf' ? '#DC2626' : '#2563EB',
}))

const FileInfo = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: '200px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}))

const ErrorAlert = styled(Alert)(({ theme }) => ({
  marginBottom: '16px',
  borderRadius: '8px',
}))

const ModalContainer = styled(Box)(({ theme }) => ({
  width: 'calc(100% - 32px)',
  maxWidth: 800,
  maxHeight: '90vh',
  backgroundColor: '#FFFFFF',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  borderRadius: '16px',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
  padding: '32px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',

  '&:focus-visible': {
    outline: 'none',
  },

  [theme.breakpoints.up('sm')]: {
    width: 800,
    padding: '40px',
  },
}))

const BoxContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
}))

const proSchema = yup.object({})

const parSchema = yup.object({})

const EditDocuments = ({
  open,
  onClose,
  onSave,
  openDelete,
  id,
  role,
  isAdmin = true,
}) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [errorTypeFiles, setErrorTypeFiles] = useState(false)
  const [errorSizeFiles, setErrorSizeFiles] = useState(false)
  const dispatch = useDispatch()
  const [fileList, setFileList] = useState()
  const [fileListType, setFileListType] = useState([])
  const [isCheckedOnlyAdmin, setIsCheckedOnlyAdmin] = useState(isAdmin)
  const { handleSubmit, setValue } = useForm({
    resolver: yupResolver(role === ROLE_ENTREPRISE ? proSchema : parSchema),
  })

  const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2 MB in bytes
  const switchToggleOnlyAdmin = () => {
    setIsCheckedOnlyAdmin(!isCheckedOnlyAdmin)
  }

  const validateSelectedFile = (selectedFile) => {
    if (!selectedFile) {
      return
    }

    const fileSizeKiloBytes = selectedFile.size

    if (fileSizeKiloBytes > MAX_FILE_SIZE) {
      return false
    }
    return true
  }

  const handleChangeType = (event, documentKey, file) => {
    if (validateSelectedFile(file)) {
      const newFileListType = [...fileListType]
      newFileListType[documentKey] = event.target.value
      setFileListType(newFileListType)
    }
  }

  const handleFilesChange = (event) => {
    /*     setFileList(event.target.files)
    setValue('file', event.target.files) */

    const files = event.target.files // Get the selected files

    const validFiles = Array.from(files).filter(
      (file) => file.size <= MAX_FILE_SIZE
    )

    if (validFiles.length < files.length) {
      setErrorSizeFiles(true)

      setTimeout(function () {
        setErrorSizeFiles(false)
      }, 3000)
      /*  alert(
        `Certains fichiers dépassent la limite autorisée (2MB) et ne seront pas inclus. Veuillez sélectionner des fichiers plus petits`
      ) */
      //event.target.files = new FileList(validFiles, event.target.files.name) // Update the file input's FileList
    }

    setFileList(validFiles)
    setValue('file', validFiles)

    if (role === ROLE_ENTREPRISE) {
      setFileListType(
        Array.from({ length: validFiles.length }, (_, i) => 'document')
      )
    } else {
      setFileListType(Array.from({ length: validFiles.length }, (_, i) => 0))
    }
  }

  const onSubmit = async (data) => {
    if (!fileList || fileList.length === 0) return
    setLoading(true)
    //return

    const {
      admin,
      candidat,
      date,
      entreprise,
      lang,
      offre,
      parent,
      password,
      status,
      ...rest
    } = data

    const formData = new FormData()
    formData.append('user', id)
    formData.append('onlyAdmin', isCheckedOnlyAdmin)
    formData.append('types', fileListType)
    Array.from(fileList).map((doc) => {
      formData.append('documents[]', doc)
    })

    if (fileListType.includes(0)) {
      setErrorTypeFiles(true)
      setLoading(false)
      setTimeout(function () {
        setErrorTypeFiles(false)
      }, 3000)
      return
    }

    setErrorTypeFiles(false)

    try {
      const result = await Api.document.add(formData)
      const handling = handlingErrors(result)

      if (handling.status === 401) {
        dispatch(userLogout())
        return
      }

      if (handling.ok) {
        //const res = handling.data
        setFileList()
        setLoading(false)
        onSave()
      } else {
        console.log('erreur')
        setLoading(false)
      }
    } catch (error) {
      console.log('erreur submit !')
    }
  }

  const getFileIcon = (fileType) => {
    if (fileType === 'application/pdf') {
      return <FontAwesomeIcon icon={faFilePdf} />
    }
    return <FontAwesomeIcon icon={faFile} />
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getFileExtension = (filename) => {
    const lastDotIndex = filename.lastIndexOf('.')
    if (lastDotIndex === -1) return 'FILE'
    return filename.slice(lastDotIndex + 1).toUpperCase()
  }

  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

  return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreen}
        scroll={'paper'}
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : '16px',
            p: fullScreen ? 0 : '8px',
            m: fullScreen ? 0 : undefined,
            maxHeight: fullScreen ? '100%' : '90vh',
            ...(fullScreen && {
              display: 'flex',
              flexDirection: 'column',
            }),
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            pb: 2,
            flexShrink: 0,
            borderBottom: '1px solid rgba(229, 231, 235, 0.8)',
            backgroundColor: 'rgba(249, 250, 251, 0.5)',
          }}
        >
          <Typography
            variant={fullScreen ? 'subtitle1' : 'h6'}
            sx={{ fontWeight: 600, color: '#1f2937', pr: 1, lineHeight: 1.3 }}
          >
          {t('box.title.addDocuments')}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#6b7280',
            '&:hover': {
              backgroundColor: 'rgba(107, 114, 128, 0.1)',
              color: '#374151',
            },
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4, pt: '32px !important' }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <InputContainer>
            <UploadArea
              onClick={() => document.getElementById('file-input').click()}
            >
              <FontAwesomeIcon
                icon={faCloudArrowUp}
                style={{
                  fontSize: '48px',
                  color: '#96A0FF',
                  marginBottom: '16px',
                }}
              />
              <Typography variant="h6" fontWeight={500} color="#374151" mb={1}>
                {t('button.downloadFiles')} (2MB)
              </Typography>
              <Typography variant="body2" color="#6B7280">
                Cliquez pour télécharger ou glissez-déposez vos fichiers ici
              </Typography>
              <input
                id="file-input"
                type="file"
                name="files"
                accept=".doc,.docx,.pdf"
                onChange={handleFilesChange}
                multiple
                style={{ display: 'none' }}
              />
            </UploadArea>

            {errorSizeFiles && (
              <ErrorAlert
                severity="error"
                icon={<FontAwesomeIcon icon={faCircleExclamation} />}
              >
                {t('errorMessage.fileError2')}
              </ErrorAlert>
            )}

            {errorTypeFiles && (
              <ErrorAlert
                severity="error"
                icon={<FontAwesomeIcon icon={faCircleExclamation} />}
              >
                {t('errorMessage.fileError3')}
              </ErrorAlert>
            )}

            {fileList && fileList.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={500}
                  color="#374151"
                  mb={2}
                >
                  Fichiers sélectionnés ({fileList.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {Array.from(fileList).map((f, key) => {
                    const isValid = validateSelectedFile(f)
                    return (
                      <FileItem key={key} isValid={isValid}>
                        <FileIcon fileType={f.type}>
                          {getFileIcon(f.type)}
                        </FileIcon>
                        <FileInfo>
                          <Typography
                            variant="body1"
                            fontWeight={500}
                            color="#1F2937"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '300px',
                            }}
                          >
                            {f.name}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Chip
                              label={getFileExtension(f.name)}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '11px', fontWeight: 600 }}
                            />
                            <Typography variant="body2" color="#6B7280">
                              {formatFileSize(f.size)}
                            </Typography>
                            {!isValid && (
                              <Chip
                                label="Taille excessive"
                                size="small"
                                color="error"
                                icon={
                                  <FontAwesomeIcon icon={faCircleExclamation} />
                                }
                              />
                            )}
                          </Box>
                        </FileInfo>
                        {role === ROLE_CANDIDAT && isValid && (
                          <FormControl
                            size="small"
                            sx={{ minWidth: 200, flexShrink: 0 }}
                          >
                            <Select
                              value={fileListType[key] || '0'}
                              onChange={(e) => handleChangeType(e, key, f)}
                              displayEmpty
                              sx={{
                                fontSize: '14px',
                                '& .MuiSelect-select': {
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                },
                              }}
                            >
                              <MenuItem value="0" sx={{ fontSize: '13px' }}>
                                {t('list.documentType')}
                              </MenuItem>
                              <MenuItem value="Cv" sx={{ fontSize: '13px' }}>
                                {t('list.documentType1')}
                              </MenuItem>
                              <MenuItem
                                value="Diplômes"
                                sx={{ fontSize: '13px' }}
                              >
                                {t('list.documentType2')}
                              </MenuItem>
                              <MenuItem
                                value="Certifications Techniques"
                                sx={{ fontSize: '13px' }}
                              >
                                {t('list.documentType3')}
                              </MenuItem>
                              <MenuItem
                                value="Lettre de motivation"
                                sx={{ fontSize: '13px' }}
                              >
                                {t('list.documentType4')}
                              </MenuItem>
                              <MenuItem
                                value="Certificat de travail"
                                sx={{ fontSize: '13px' }}
                              >
                                {t('list.documentType5')}
                              </MenuItem>
                              <MenuItem
                                value="Recommandations"
                                sx={{ fontSize: '13px' }}
                              >
                                {t('list.documentType7')}
                              </MenuItem>
                              <MenuItem
                                value="References"
                                sx={{ fontSize: '13px' }}
                              >
                                {t('list.documentType7')}
                              </MenuItem>
                              <MenuItem
                                value="Autres"
                                sx={{ fontSize: '13px' }}
                              >
                                {t('list.documentType8')}
                              </MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </FileItem>
                    )
                  })}
                </Box>
              </Box>
            )}

            {isAdmin && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: '#F9FAFB',
                  borderRadius: '8px',
                }}
              >
                <FormControlLabel
                  control={
                    <SwitchButton
                      label=""
                      defaultChecked={isCheckedOnlyAdmin}
                      onChange={switchToggleOnlyAdmin}
                    />
                  }
                  label="Afficher que pour l'admin"
                  sx={{ mb: 1 }}
                />
                {!isCheckedOnlyAdmin && (
                  <Typography
                    variant="caption"
                    color="#6B7280"
                    sx={{ display: 'block', ml: 4 }}
                  >
                    {t('errorMessage.fileError4')}
                  </Typography>
                )}
              </Box>
            )}
          </InputContainer>
        </form>
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          py: 3,
          gap: 2,
          justifyContent: 'center',
          backgroundColor: 'rgba(249, 250, 251, 0.8)',
          borderTop: '1px solid rgba(229, 231, 235, 0.8)',
          flexDirection: { xs: 'column', sm: 'row' },
          flexShrink: 0,
          '& > *': {
            width: { xs: '100%', sm: 'auto' },
            m: 0,
          },
        }}
      >
        <OutlineButton onClick={onClose} fullWidth={fullScreen}>{t('button.cancel')}</OutlineButton>
        <GradientButton
          onClick={handleSubmit(onSubmit)}
          disabled={!fileList || fileList.length === 0}
          fullWidth={fullScreen}
        >
          {loading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
              {t('profile.saving')}
            </>
          ) : (
            t('button.save')
          )}
        </GradientButton>
      </DialogActions>
    </Dialog>
  )
}

export default EditDocuments
